import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { StarMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  { id: "shipping", label: "How fast is shipping?" },
  { id: "trial", label: "What's the 30-night trial?" },
  { id: "kids", label: "Is it safe for kids?" },
];

export function Concierge() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("astra_chat_session") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("astra_chat_session", id);
        return id;
      })()
  );

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6).trim();
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            const token = j.t || j.error || "";
            if (token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Nova lost the signal — please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="concierge-open-button"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ delay: 1.6, type: "spring", stiffness: 200, damping: 16 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-24 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-cyan-400 shadow-[0_0_30px_rgba(99,102,241,0.5)] transition-transform hover:scale-110 active:scale-95"
            aria-label="Chat with Nova, the AI concierge"
          >
            <MessageCircle className="h-6 w-6 text-[#07090e]" />
            <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-[#07090e] bg-emerald-400" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-50 flex h-[60vh] max-h-[540px] w-[calc(100%-2.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#0e131f] shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-[#131a29] px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500/40 to-cyan-400/30">
                <StarMark className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-display text-sm font-bold text-white">Nova — AI Concierge</p>
                <p className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-soft" /> Online, answers instantly
                </p>
              </div>
              <button data-testid="concierge-close-button" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={listRef} data-testid="concierge-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-white/10 bg-[#1a2235] px-4 py-3 text-sm leading-relaxed text-slate-200">
                Hey, I'm Nova. Ask me anything about the 7-Night Sleep Reset — what's inside, the guarantee, instant download — or tell me what your nights look like.
              </div>
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {CHIPS.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`concierge-chip-${c.id}`}
                      onClick={() => send(c.label)}
                      className="rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3.5 py-1.5 text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/15"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`concierge-message-${m.role}-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-tr-sm bg-cyan-400 text-[#07090e] font-medium"
                      : "rounded-tl-sm border border-white/10 bg-[#1a2235] text-slate-200"
                  }`}
                >
                  {m.content || <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 p-3">
              <div className="flex items-center gap-2 rounded-full border border-white/15 bg-[#0b0f18] px-2 py-1.5">
                <input
                  data-testid="concierge-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask Nova anything…"
                  className="flex-1 bg-transparent px-3 text-base text-white placeholder-slate-600 outline-none"
                />
                <button
                  data-testid="concierge-send-button"
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-400 text-[#07090e] transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
